<template>
  <section
    v-if="$auth.isAuthenticated"
    class="w-full"
  >
    <h1 class="mb-6 text-2xl font-bold">
      Kundenstatus
    </h1>

    <div class="flex flex-wrap gap-4">
      <CustomerStatusBox
        title="Kontakt Akquise"
        :rows="[
          { label: 'Reaktivierung bestehender Kontakte', value: totalInvitationsCount?.toLocaleString() || 0 },
          { label: 'Gewinnung neuer Kontakte', value: `${savedContentsTotal?.toLocaleString() || 0} / ${savedContentsNumberOfPosts?.toLocaleString() || 0}` }
        ]"
        :footer="{ label: 'Zeit- und Kostenersparnis:', value: formatMinutesToHours(timeSaved || 0) }"
      />

      <CustomerStatusBox
        title="Automatische E-Mail Funnels"
        :rows="[
          { label: 'gesendet:', value: sent?.toLocaleString() || 0 },
          { label: 'geöffnet:', value: uniqueOpens?.toLocaleString() || 0 },
          { label: 'Klicks:', value: uniqueLinksClicked?.toLocaleString() || 0 },
          { label: 'Zeit- und Kostenersparnis:', value: `${formatMinutesToHours(timeSaved || 0)} | ${formatPrice(moneySaved || 0)}` }
        ]"
      />

      <CustomerStatusBox
        title="Status Ihrer Landingpages/Widgets"
        :rows="[
          { label: 'Aufrufe', value: analyticsImpression?.toLocaleString() || 0 },
          { label: '# Landingpages/Widgets', value: numberOfLandingpages?.toLocaleString() || 0 }
        ]"
        :footer="{ label: 'Zeit- und Kostenersparnis:', value: `${formatMinutesToHours(timeSaved || 0)} | ${formatPrice(moneySaved || 0)}` }"
      />

      <CustomerStatusBox
        title="Insgesamt gewonnene Kontakte"
        :rows="[
          { label: 'Alle', value: totalLeadCount?.toLocaleString() || 0 },
          { label: 'Mit Bewertung', value: totalLeadCountWithValuation?.toLocaleString() || 0 },
          { label: 'Letzte 30 Tage', value: totalLeadCountLastThirtyDays?.toLocaleString() || 0 }
        ]"
      />

      <CustomerStatusBox
        title="Das haben Sie mit BOTTIMMO insgesamt erreicht"
        :rows="[
          { label: 'Immobilienvolumen', value: totalLeadValue || 0 },
          { label: 'Potenzielle Provision - ', value: `${commission}% - ${totalCommissionValue || 0}` }
        ]"
        :footer="{ label: 'Gesamte Zeit- und Kostenersparnis:', value: `${formatMinutesToHours(timeSaved || 0)} | ${formatPrice(moneySaved || 0)}` }"
      />
    </div>
  </section>
</template>
<script>
import STATISTICS from '@/graphql/Statistics.gql'
import GET_SAVED_CONTENTS from '@/graphql/GetSavedContents.gql'
import INVITATION_STATISTICS_FOR_COMPANY from '@/graphql/InvitationStatisticsForCompany.gql'
import EMAIL_FUNNELS_STATISTICS from '@/graphql/EmailFunnelsStatistics.gql'
import GET_NUMBER_OF_LANDINGPAGES from '@/graphql/GetNumberOfLandingpages.gql'
import GET_ANALYTICS_IMPRESSIONS from '@/graphql/GetAnalyticsImpressionForCompany.gql'
import CustomerStatusBox from './customers-status-box.vue'

const MINUTES_PER_POST = 45
const MONEY_PER_MINUTE = 2.5
const MINUTES_PER_MAIL = 25

export default {
  components: {
    CustomerStatusBox
  },
  props: {
    companyId: {
      type: String,
      required: true
    }
  },
  data: () => ({
    totalLeadCount: 0,
    totalLeadCountLastThirtyDays: 0,
    totalLeadCountWithValuation: 0,
    totalLeadValue: 0,
    totalCommissionValue: 0,
    commission: 0,
    savedContentsTotal: 0,
    savedContentsNumberOfPosts: 0,
    totalInvitationsCount: 0,
    timeSaved: 0,
    moneySaved: 0,
    sent: 0,
    uniqueOpens: 0,
    uniqueLinksClicked: 0,
    numberOfLandingpages: 0,
    analyticsImpression: 0
  }),
  computed: {
    contentCreatorConfig () {
      return this.$features.feature(this.featureNames?.CONTENT_CREATOR)?.config?.dashboard
    },
    acquisitionBoosterConfig () {
      return this.$features.feature(this.featureNames?.ACQUISITION_BOOSTER)?.config?.dashboard
    }
  },

  async created () {
    const apolloClient = this.$apollo.provider.defaultClient

    try {
      let invitationsTimeSaved = 0
      let invitationsMoneySaved = 0
      let savedContentsTimeSaved = 0
      let savedContentsMoneySaved = 0
      // 1. Fetch contents
      const { data: contentsData } = await apolloClient.query({
        query: GET_SAVED_CONTENTS,
        variables: {
          input: {
            companyId: this.companyId,
            perPage: 1,
            page: 1
          }
        },
        fetchPolicy: 'network-only'
      })

      if (contentsData?.savedContents) {
        const factors = {
          minutesPerPost: this.contentCreatorConfig?.minutesPerPost || MINUTES_PER_POST,
          moneyPerMinute: this.contentCreatorConfig?.moneyPerMinute || MONEY_PER_MINUTE
        }

        this.savedContentsTotal = contentsData?.savedContents.total
        this.savedContentsNumberOfPosts = contentsData?.savedContents.numberOfPosts
        savedContentsTimeSaved = this.savedContentsTotal * factors.minutesPerPost
        savedContentsMoneySaved = savedContentsTimeSaved * factors.moneyPerMinute
      }

      // 2. Fetch invitation stats
      const { data: invitationData } = await apolloClient.query({
        query: INVITATION_STATISTICS_FOR_COMPANY,
        variables: { companyId: this.companyId },
        fetchPolicy: 'network-only'
      })

      if (invitationData?.invitationStatisticsForCompany) {
        const { minutesPerMail = MINUTES_PER_MAIL, moneyPerMinute = MONEY_PER_MINUTE } = this.acquisitionBoosterConfig || {}

        this.totalInvitationsCount = invitationData.invitationStatisticsForCompany.total
        invitationsTimeSaved = this.totalInvitationsCount * minutesPerMail
        invitationsMoneySaved = invitationsTimeSaved * moneyPerMinute
      }

      // 3. Fetch statistics
      const { data: statisticsData } = await apolloClient.query({
        query: STATISTICS,
        variables: { companyId: this.companyId },
        fetchPolicy: 'network-only'
      })

      if (statisticsData?.statistics) {
        this.totalLeadCount = statisticsData.statistics.totalLeadCount.total
        this.totalLeadCountLastThirtyDays = statisticsData.statistics.totalLeadCount.lastThirtyDays
        this.totalLeadCountWithValuation = statisticsData.statistics.totalLeadCount.withValuation
        this.totalLeadValue = this.formatPrice(statisticsData.statistics.totalLeadValue)
        this.totalCommissionValue = this.formatPrice(statisticsData.statistics.totalCommissionValue.value)
        this.commission = this.formatPrice(statisticsData.statistics.totalCommissionValue.commission)
      }

      // Final combined savings
      this.timeSaved = savedContentsTimeSaved + invitationsTimeSaved
      this.moneySaved = savedContentsMoneySaved + invitationsMoneySaved

      const { data: landingpages } = await apolloClient.query({
        query: GET_NUMBER_OF_LANDINGPAGES,
        variables: { companyId: this.companyId },
        fetchPolicy: 'network-only'
      })

      if (landingpages) {
        this.numberOfLandingpages = landingpages.numberOfLandingpages
      }

      const { data: analytics } = await apolloClient.query({
        query: GET_ANALYTICS_IMPRESSIONS,
        variables: { companyId: this.companyId },
        fetchPolicy: 'network-only'
      })

      if (analytics) {
        this.analyticsImpression = analytics.analyticsImpression
      }

      const { data: emailFunnels } = await apolloClient.query({
        query: EMAIL_FUNNELS_STATISTICS,
        variables: { companyId: this.companyId },
        fetchPolicy: 'network-only'
      })

      if (emailFunnels?.companyEmailFunnelsStatistics) {
        this.sent = emailFunnels?.companyEmailFunnelsStatistics?.sent
        this.uniqueOpens = emailFunnels?.companyEmailFunnelsStatistics?.uniqueOpens
        this.uniqueLinksClicked = emailFunnels?.companyEmailFunnelsStatistics?.uniqueLinksClicked
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  },
  methods: {
    formatMinutesToHours (minutes) {
      return `${Math.round(minutes / 60).toLocaleString()} h`
    }
  }
}
</script>
